import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { SupabaseClientService } from '@services/db/supabase-client';
import { environment } from '../../../environments/environment';

/**
 * BrowserUse API Authentication Interceptor
 * 
 * Automatically adds JWT token to all requests to BrowserUse API (localhost:4488)
 * 
 * Features:
 * - Automatic JWT token injection
 * - Authentication check
 * - Error handling for unauthenticated users
 * - Transparent operation - no changes needed in services
 * 
 * Architecture:
 * - Follows Angular best practices for interceptors
 * - Located in core/interceptors for proper separation of concerns
 * - Uses dependency injection for SupabaseClientService
 * - Handles async token retrieval properly
 */
export const browserUseAuthInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  // Check if this request is for BrowserUse API
  if (!isBrowserUseApiRequest(req.url)) {
    // Not a BrowserUse API request - pass through
    return next(req);
  }

  const supabase = inject(SupabaseClientService);

  // Get authentication token and add to request
  return from(getAuthToken(supabase)).pipe(
    switchMap(token => {
      // Clone request with Authorization header
      const authReq = req.clone({
        setHeaders: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return next(authReq);
    }),
    catchError(error => {
      console.error('[BrowserUse Auth Interceptor] Error:', error);
      return throwError(() => error);
    })
  );
};

/**
 * Check if the request URL is for BrowserUse API
 */
function isBrowserUseApiRequest(url: string): boolean {
  return url.startsWith(environment.browserUse.apiUrl);
}

/**
 * Get JWT token from Supabase session
 * 
 * @param supabase - Supabase client service
 * @returns Promise with JWT token
 * @throws Error if user is not authenticated
 */
async function getAuthToken(supabase: SupabaseClientService): Promise<string> {
  const session = await supabase.client.auth.getSession();
  const token = session.data.session?.access_token;

  if (!token) {
    throw new Error('User is not authenticated. Please sign in to access BrowserUse API.');
  }

  return token;
}
